import { useCallback } from 'react';
import { useGlobalStore } from '../../application/store/store';

/**
 * Hook that provides a function to reset all organization-specific cached data.
 * Should be called when switching organizations to ensure no stale data remains.
 */
export const useOrganizationReset = () => {
    const resetOverviews = useGlobalStore((state) => state.resetOverviews);
    const clearUserCache = useGlobalStore((state) => state.clearUserCache);
    const clearInactiveUserCache = useGlobalStore(
        (state) => state.clearInactiveUserCache
    );
    const signalUserListReload = useGlobalStore(
        (state) => state.signalUserListReload
    );
    const resetAvailableStorage = useGlobalStore(
        (state) => state.resetAvailableStorage
    );
    const setAvailableDrugs = useGlobalStore(
        (state) => state.setAvailableDrugs
    );
    const resetSelectedDrug = useGlobalStore(
        (state) => state.resetSelectedDrug
    );
    const resetDrugsInStorage = useGlobalStore(
        (state) => state.resetDrugsInStorage
    );
    const resetTransactionFormData = useGlobalStore(
        (state) => state.resetTransactionFormData
    );
    const resetDisposedFormData = useGlobalStore(
        (state) => state.resetDisposedFormData
    );
    const resetShiftChangeInventoryFormData = useGlobalStore(
        (state) => state.resetShiftChangeInventoryFormData
    );
    const resetPatient = useGlobalStore((state) => state.resetPatient);
    const resetShouldBeInventory = useGlobalStore(
        (state) => state.resetShouldBeInventory
    );
    const resetInventoryRecords = useGlobalStore(
        (state) => state.resetInventoryRecords
    );
    const resetInventoryList = useGlobalStore(
        (state) => state.resetInventoryList
    );
    const resetAmbulanceFormData = useGlobalStore(
        (state) => state.resetAmbulanceFormData
    );

    const resetOrganizationData = useCallback(() => {
        // Form overviews and transaction counters
        resetOverviews();

        // User caches
        clearUserCache();
        clearInactiveUserCache();
        signalUserListReload?.();

        // Storage and drug data
        resetAvailableStorage();
        setAvailableDrugs([]);
        resetSelectedDrug();
        resetDrugsInStorage();

        // Transaction forms
        resetTransactionFormData();
        resetDisposedFormData();
        resetAmbulanceFormData();

        // Inventory data
        resetShiftChangeInventoryFormData();
        resetShouldBeInventory();
        resetInventoryRecords();
        resetInventoryList();

        // Patient data
        resetPatient();
    }, [
        resetOverviews,
        clearUserCache,
        clearInactiveUserCache,
        signalUserListReload,
        resetAvailableStorage,
        setAvailableDrugs,
        resetSelectedDrug,
        resetDrugsInStorage,
        resetTransactionFormData,
        resetDisposedFormData,
        resetAmbulanceFormData,
        resetShiftChangeInventoryFormData,
        resetShouldBeInventory,
        resetInventoryRecords,
        resetInventoryList,
        resetPatient,
    ]);

    return { resetOrganizationData };
};
